import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "icon", "iconSize", "size", "use", "contentStyles"];
import { useEffect, useRef, useState } from 'react';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { getIconSize, getVariantStyles } from './DropZone.helpers';
import styled, { useTheme } from 'foundations-theming/utils';
import { Flex } from '../../../components/Flex';

// 128
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Outer = styled.div.withConfig({
  displayName: "DropZone__Outer",
  componentId: "sc-1vkc3pl-0"
})(({
  $cssStyles,
  $variantStyles,
  theme
}) => {
  return Object.assign({
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.borderRadius['200']
  }, $variantStyles, $cssStyles);
});
const Content = styled.div.withConfig({
  displayName: "DropZone__Content",
  componentId: "sc-1vkc3pl-1"
})(({
  $size,
  theme,
  $contentStyles = {}
}) => {
  return Object.assign({
    display: 'flex',
    flexDirection: $size === 'sm' ? 'row' : 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    minHeight: $size === 'sm' ? '72px' : '198px',
    paddingBlock: theme.space['600'],
    paddingInline: theme.space['500']
  }, $size === 'sm' ? {
    columnGap: theme.space['300']
  } : {
    rowGap: theme.space['300']
  }, $contentStyles);
});
export const DropZone = _ref => {
  let {
      children,
      icon: Icon,
      iconSize,
      size = 'small',
      use = 'default',
      contentStyles = {}
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const theme = useTheme();
  const _contentEl = useRef(null);
  const [__contentElOffsetWidth, setContentElOffsetWidth] = useState(0);
  useEffect(() => {
    const handleResize = () => {
      if (_contentEl.current) {
        setContentElOffsetWidth(_contentEl.current.offsetWidth);
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  const variantStyles = getVariantStyles(use, theme);
  const {
    tokenProps,
    domProps,
    callbackProps
  } = filterProps(rest);
  const outerCssStyles = useMapTokenPropsToCSS(tokenProps);
  const contentCssStyles = useMapTokenPropsToCSS(contentStyles);
  const shorthandSize = size === 'small' ? 'sm' : 'lg';
  let tshirtIconSize = typeof iconSize === 'number' ? 'small' : iconSize;
  if (tshirtIconSize === null || tshirtIconSize === undefined) {
    // `iconSize` does not align with the `size` prop, these icon sizes are
    // based on figma and match UIDropZone for backwards compatibility
    tshirtIconSize = size === 'small' ? 'xs' : 'sm';
  }
  return /*#__PURE__*/_jsx(Outer, Object.assign({
    $cssStyles: outerCssStyles,
    $variantStyles: variantStyles
  }, domProps, callbackProps, {
    children: /*#__PURE__*/_jsxs(Content, {
      $size: shorthandSize,
      $contentStyles: contentCssStyles,
      ref: _contentEl,
      children: [Icon && /*#__PURE__*/_jsx(Icon, {
        size: getIconSize(tshirtIconSize),
        color: variantStyles.iconColor
      }), /*#__PURE__*/_jsx(Flex, {
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        children: children
      })]
    })
  }));
};